// Runtime data-binding — moved to `@twasta/page-runtime/dataBinding`.
//
// The package's `fetchDataSource` takes a `DataFetcher` parameter so the
// standalone runtime can supply its own (fetch + API key). The designer
// uses the platform's axios-backed website API. We curry it here so
// existing callers (RuntimeContext) keep the simpler signature.

import { previewData, publicData, siteData } from '@/api/website'
import type { PageDataSource, ResolvedDataEntry } from '@/pages/designers/page/types'
import {
  fetchDataSource as packageFetchDataSource,
  rowsFromEntry,
  seedResources,
  type DataAccess,
  type DataFetcher,
} from '@twasta/page-runtime/dataBinding'

export { rowsFromEntry, seedResources }
export type { DataAccess, DataFetcher }

// Platform fetcher — adapts the axios calls (which return
// AxiosResponse<{data: ResolvedDataEntry}>) to the package's
// {data: ResolvedDataEntry} shape.
const platformFetcher: DataFetcher = {
  preview: (project, source, args, scopes) =>
    previewData(project, source as unknown, args, scopes) as Promise<{ data: ResolvedDataEntry }>,
  public_: (publicKey, pageObjName, dsId, args, scopes) =>
    publicData(publicKey, pageObjName, dsId, args, scopes) as Promise<{ data: ResolvedDataEntry }>,
  // Site preview: end-user-scoped fetch. The visitor's session token is read
  // from localStorage inside `siteData` (keyed per user/project), so the
  // fetcher stays stateless and the runtime needs no token wiring.
  site: (userId, project, source, args, scopes) =>
    siteData(userId, project, source as unknown, args, scopes) as Promise<{ data: ResolvedDataEntry }>,
}

// Curried for back-compat: existing callers pass (access, source, args, scopes).
export function fetchDataSource(
  access: DataAccess,
  source: PageDataSource,
  args?: Record<string, unknown>,
  scopes?: Record<string, unknown>,
  opts?: { throwOnError?: boolean },
) {
  return packageFetchDataSource(platformFetcher, access, source, args, scopes, opts)
}
